"use client";

import { useEffect } from "react";
import {
  collectLaunchDarklyClientContext,
  resolveClientRoute,
} from "@/lib/observability/collect-launchdarkly-context";
import { reportWebObservabilityIncident } from "@/lib/observability/report-incident";
import {
  collectClientDeviceContext,
  isIgnorableClientError,
  sanitizeObservabilityLogSnapshot,
  sanitizeObservabilityMessage,
  sanitizeObservabilityStack,
} from "@/lib/observability/sanitize-observability-payload";
import { useInternalAuth } from "@/providers/auth-provider";

const reportedFingerprints = new Set<string>();

const buildFingerprint = (input: {
  message: string;
  route: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  type?: string;
}): string =>
  [
    input.type ?? "error",
    input.message.trim(),
    input.route,
    input.filename ?? "",
    input.lineno ?? "",
    input.colno ?? "",
  ].join("|");

const resolveErrorDetails = (
  error: unknown,
): { message?: string; name?: string; stack?: string } => {
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  return {};
};

export const reportObservabilityClientError = async (input: {
  message: string;
  stack?: string;
  userId?: string;
  userEmail?: string;
  logSnapshot?: Record<string, unknown>;
}): Promise<void> => {
  const route = resolveClientRoute();
  const message = sanitizeObservabilityMessage(input.message);
  const filename =
    typeof input.logSnapshot?.filename === "string"
      ? input.logSnapshot.filename
      : undefined;
  const lineno =
    typeof input.logSnapshot?.lineno === "number"
      ? input.logSnapshot.lineno
      : undefined;
  const colno =
    typeof input.logSnapshot?.colno === "number"
      ? input.logSnapshot.colno
      : undefined;
  const type =
    typeof input.logSnapshot?.type === "string"
      ? input.logSnapshot.type
      : undefined;

  if (isIgnorableClientError({ message, filename })) {
    return;
  }

  const fingerprint = buildFingerprint({
    message,
    route,
    filename,
    lineno,
    colno,
    type,
  });

  if (reportedFingerprints.has(fingerprint)) {
    return;
  }
  reportedFingerprints.add(fingerprint);

  const ldContext = await collectLaunchDarklyClientContext();
  const logSnapshot = sanitizeObservabilityLogSnapshot({
    ...collectClientDeviceContext(),
    ...input.logSnapshot,
  });

  await reportWebObservabilityIncident({
    message,
    stack: sanitizeObservabilityStack(input.stack),
    route,
    sessionReplayUrl: ldContext.sessionReplayUrl ?? undefined,
    sessionReplayUrlAtError: ldContext.sessionReplayUrlAtError ?? undefined,
    serviceName: ldContext.serviceName,
    environment: ldContext.environment,
    appVersion: ldContext.appVersion,
    userId: input.userId,
    userEmail: input.userEmail,
    logSnapshot,
  });
};

export const ClientErrorReporter = () => {
  const { user } = useInternalAuth();

  useEffect(() => {
    const handleError = (event: ErrorEvent): void => {
      const errorDetails = resolveErrorDetails(event.error);
      const message =
        errorDetails.message ||
        event.message ||
        "Erro não tratado no browser";

      void reportObservabilityClientError({
        message,
        stack: errorDetails.stack,
        userId: user?.id,
        userEmail: user?.email ?? undefined,
        logSnapshot: {
          type: "window.error",
          errorName: errorDetails.name,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const handleRejection = (event: PromiseRejectionEvent): void => {
      const reason = event.reason;
      const errorDetails = resolveErrorDetails(reason);
      const message =
        errorDetails.message ||
        (typeof reason === "string" ? reason : "Promise rejection não tratada");

      void reportObservabilityClientError({
        message,
        stack: errorDetails.stack,
        userId: user?.id,
        userEmail: user?.email ?? undefined,
        logSnapshot: {
          type: "unhandledrejection",
          errorName: errorDetails.name,
          reasonType: typeof reason,
        },
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [user?.email, user?.id]);

  return null;
};
